import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
} from "@mui/material";
import {
    Add as AddIcon,
    Category as CategoryIcon,
    Business as BusinessIcon,
} from "@mui/icons-material";
import { productService } from "../../../services/productService";
import { Product, Category, Brand } from "../../../types";
import { formatCurrencyBDT } from "../../../utils/currency";

interface QuickProductDialogProps {
    open: boolean;
    onClose: () => void;
    searchTerm: string;
    onProductCreated: (product: Product) => void;
}

export const QuickProductDialog: React.FC<QuickProductDialogProps> = ({
    open,
    onClose,
    searchTerm,
    onProductCreated,
}) => {
    const [productName, setProductName] = useState<string>(searchTerm);
    const [price, setPrice] = useState<string>("");
    const [initialStock, setInitialStock] = useState<string>("10"); // Default to 10 units
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [categories, setCategories] = useState<Category[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
    const [selectedBrandId, setSelectedBrandId] = useState<string>("");
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingBrands, setLoadingBrands] = useState(false);

    // Reset form when dialog opens with new search term
    useEffect(() => {
        if (open) {
            setProductName(searchTerm);
            setPrice("");
            setInitialStock("10"); // Reset to default 10 units
            setError("");
            setSelectedCategoryId("");
            setSelectedBrandId("");
        }
    }, [open, searchTerm]);

    // Fetch categories and brands when dialog opens
    useEffect(() => {
        if (open) {
            fetchCategoriesAndBrands();
        }
    }, [open]);

    const fetchCategoriesAndBrands = async () => {
        try {
            setLoadingCategories(true);
            setLoadingBrands(true);

            const [categoriesData, brandsData] = await Promise.all([
                productService.getCategories({ active_only: true }),
                productService.getBrands({ active_only: true }),
            ]);

            setCategories(categoriesData);
            setBrands(brandsData);

            // Pre-select first options if available
            if (categoriesData.length > 0) {
                setSelectedCategoryId(categoriesData[0].category_id);
            }
            if (brandsData.length > 0) {
                setSelectedBrandId(brandsData[0].brand_id);
            }
        } catch (err: any) {
            console.error("Failed to load categories or brands:", err);
        } finally {
            setLoadingCategories(false);
            setLoadingBrands(false);
        }
    };

    const handleCreateProduct = async () => {
        if (!productName.trim()) {
            setError("Product name is required");
            return;
        }

        const priceValue = parseFloat(price);
        if (isNaN(priceValue) || priceValue <= 0) {
            setError("Please enter a valid price");
            return;
        }

        const stockValue = parseInt(initialStock);
        if (isNaN(stockValue) || stockValue < 0) {
            setError("Please enter a valid initial stock quantity");
            return;
        }

        try {
            setLoading(true);
            setError("");

            // Create product data with selected category, brand, and initial stock
            const productData = {
                name: productName.trim(),
                selling_price: priceValue,
                cost_price: priceValue * 0.7, // Default 30% markup
                sku: `POS-${Date.now()}`, // Generate unique SKU
                is_active: true,
                category_id: selectedCategoryId || "", // Use selected category or empty string
                brand_id: selectedBrandId || undefined, // Use selected brand or undefined
                // Initial stock for immediate availability
                initial_stock: stockValue,
                min_stock_level: Math.max(1, Math.floor(stockValue * 0.2)), // 20% of initial stock
                max_stock_level: Math.floor(stockValue * 2), // 2x initial stock
            };

            const response = await productService.createProduct(productData);

            // Call the callback with the created product
            onProductCreated(response);

            // Close dialog
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to create product");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleCreateProduct();
        } else if (e.key === "Escape") {
            e.preventDefault();
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AddIcon />
                    Quick Add Product
                </Box>
            </DialogTitle>
            <DialogContent>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                >
                    Product not found? Add it quickly for this sale.
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Product Name"
                        value={productName}
                        onChange={(e) => setProductName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Enter product name"
                    />

                    <Box sx={{ display: "flex", gap: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Category (Optional)</InputLabel>
                            <Select
                                value={selectedCategoryId}
                                onChange={(e) =>
                                    setSelectedCategoryId(e.target.value)
                                }
                                label="Category (Optional)"
                                disabled={loadingCategories}
                                startAdornment={
                                    loadingCategories ? (
                                        <InputAdornment position="start">
                                            <CircularProgress size={20} />
                                        </InputAdornment>
                                    ) : (
                                        <InputAdornment position="start">
                                            <CategoryIcon />
                                        </InputAdornment>
                                    )
                                }
                            >
                                <MenuItem value="">
                                    <em>No Category</em>
                                </MenuItem>
                                {categories.map((category) => (
                                    <MenuItem
                                        key={category.category_id}
                                        value={category.category_id}
                                    >
                                        {category.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Brand (Optional)</InputLabel>
                            <Select
                                value={selectedBrandId}
                                onChange={(e) =>
                                    setSelectedBrandId(e.target.value)
                                }
                                label="Brand (Optional)"
                                disabled={loadingBrands}
                                startAdornment={
                                    loadingBrands ? (
                                        <InputAdornment position="start">
                                            <CircularProgress size={20} />
                                        </InputAdornment>
                                    ) : (
                                        <InputAdornment position="start">
                                            <BusinessIcon />
                                        </InputAdornment>
                                    )
                                }
                            >
                                <MenuItem value="">
                                    <em>No Brand</em>
                                </MenuItem>
                                {brands.map((brand) => (
                                    <MenuItem
                                        key={brand.brand_id}
                                        value={brand.brand_id}
                                    >
                                        {brand.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <TextField
                        fullWidth
                        label="Initial Stock Quantity"
                        type="number"
                        value={initialStock}
                        onChange={(e) => setInitialStock(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="10"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    📦
                                </InputAdornment>
                            ),
                        }}
                        inputProps={{
                            min: 0,
                            step: 1,
                        }}
                        helperText="How many units should be available for immediate sale?"
                    />

                    <TextField
                        fullWidth
                        label="Selling Price"
                        type="number"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="0.00"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    ৳
                                </InputAdornment>
                            ),
                        }}
                        inputProps={{
                            min: 0,
                            step: 0.01,
                        }}
                        helperText="Enter the price to charge customers"
                    />

                    {price && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            <Typography variant="body2">
                                <strong>Estimated Cost Price:</strong>{" "}
                                {formatCurrencyBDT(parseFloat(price) * 0.7)}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                This is a rough estimate. You can update the
                                actual cost in product management later.
                            </Typography>
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleCreateProduct}
                    variant="contained"
                    startIcon={<AddIcon />}
                    disabled={
                        loading ||
                        !productName.trim() ||
                        !price ||
                        !initialStock ||
                        loadingCategories ||
                        loadingBrands
                    }
                >
                    {loading ? "Creating..." : "Add to Sale"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
